import { CloseButton, HStack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FEATURE_FLAGS } from '@bower/shared/constants/featureFlags';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useFeatureFlag } from '../../../hooks/analytics/useFeatureFlag';
import type { ChatMessage } from '../../../hooks/data/agentChatReducer';
import { SessionRatingThumbs } from './SessionRatingThumbs';
import {
  isSessionRatingAnswered,
  markSessionRatingAnswered,
  takeFirstShow,
} from './sessionRatingStore';
import {
  submitSessionRating,
  trackSessionRating,
  type SessionRatingValue,
} from './submitSessionRating';

interface ChatSessionRatingProps {
  readonly sessionId: string;
  readonly lastMessage: ChatMessage | undefined;
  readonly isStreaming: boolean;
}

/**
 * The chat half of the session-end rating (`session-end-rating-enabled`, agent
 * quality plan M3): one quiet row under the LAST reply asking how the chat
 * went. One tap scores that reply's trace as `user-feedback`, which the weekly
 * agent-quality helper rolls up to the session.
 *
 * Asked at most once per conversation: rating or closing it answers it for
 * good (`sessionRatingStore`). It follows the newest reply until answered,
 * never shows while a reply streams, and never blocks the composer. Off, it
 * renders nothing.
 */
export function ChatSessionRating({ sessionId, lastMessage, isStreaming }: ChatSessionRatingProps) {
  const enabled = useFeatureFlag(FEATURE_FLAGS.SESSION_END_RATING_ENABLED);
  const [submitting, setSubmitting] = useState(false);
  // Keyed by the trace it thanked, so the next reply never inherits it.
  const [thankedTraceId, setThankedTraceId] = useState<string | null>(null);
  // Re-read the store on an answer: it is module state, not React state.
  const [, setAnswerTick] = useState(0);

  const traceId =
    lastMessage?.role === 'assistant' && lastMessage.content ? lastMessage.traceId : undefined;
  const visible =
    enabled && !isStreaming && !!sessionId && !!traceId && !isSessionRatingAnswered(sessionId);

  useEffect(() => {
    if (visible && traceId && takeFirstShow(sessionId)) {
      trackSessionRating(ANALYTICS_EVENTS.SESSION_RATING_SHOWN, {
        surface: 'chat',
        sessionId,
        traceId,
      });
    }
  }, [visible, sessionId, traceId]);

  if (thankedTraceId && thankedTraceId === traceId) {
    return (
      <Text fontSize="xs" color="text.muted" mt={2} role="status">
        Thanks, that helps us improve Bower.
      </Text>
    );
  }
  if (!visible || !traceId) return null;

  const rate = (value: SessionRatingValue) => {
    if (submitting) return;
    setSubmitting(true);
    void submitSessionRating({ surface: 'chat', sessionId, traceId, value }).then((ok) => {
      setSubmitting(false);
      // A refused rating leaves the row up, so the tap can be tried again.
      if (!ok) return;
      markSessionRatingAnswered(sessionId);
      setThankedTraceId(traceId);
    });
  };

  const dismiss = () => {
    markSessionRatingAnswered(sessionId);
    trackSessionRating(ANALYTICS_EVENTS.SESSION_RATING_DISMISSED, {
      surface: 'chat',
      sessionId,
      traceId,
    });
    setAnswerTick((n) => n + 1);
  };

  return (
    <HStack spacing={2} mt={2} data-testid="chat-session-rating">
      <Text fontSize="xs" color="text.muted">
        How did this chat go?
      </Text>
      <SessionRatingThumbs subject="chat" isDisabled={submitting} onRate={rate} />
      <CloseButton size="sm" aria-label="Not now" color="text.muted" onClick={dismiss} />
    </HStack>
  );
}
